import { useMemo } from "react";
import type { CertificationJobMapping, HrisRawEmployeeRow, Job, NurseRole, Position } from "../../../../../../utils/models";
import { normalizeRoleString } from "../utils";

/**
 * Row shape consumed by the LEFT pane (`HrisRawEmployees`) — one row per role_string,
 * joining the resolved cert mapping with the raw HRIS employees that share that role string.
 *
 * `isOrphan` rows are role strings present in raw HRIS data with no cert mapping at all
 * (no default, no HRIS, no override). They are surfaced so admins can create the mapping.
 *
 * The right pane (`HrisJobOverview`) does NOT use this — it consumes `CertificationJobMapping[]`
 * directly (mirroring the Raw Table grid).
 */
export type RoleStringRow = {
	roleString: string;
	position: Position | null;
	role: NurseRole | null;
	/** Ordered: jobs[0] is the job the role string syncs as; the rest are additional jobs (DAT-466). Empty for DONT_SYNC and orphan rows. */
	jobs: Job[];
	isOrphan: boolean;
	rawEmployees: HrisRawEmployeeRow[];
	internalDepartments: string[];
};

/** Backend-aligned normalization (see utils.tsx) — required so raw HRIS role strings join correctly with mapping rows. */
const norm = normalizeRoleString;

/**
 * Builds the row set rendered by the LEFT pane of the Pretty Mapping view (`HrisRawEmployees`).
 *
 * Rules:
 * 1. Group raw employees by normalized roleString (backend-aligned). Each bucket carries its
 *    employees + the distinct internal_department values seen for that role_string.
 * 2. For each cert mapping: emit a RoleStringRow with isOrphan=false. If a raw bucket exists
 *    for the same key, attach its employees + internal_departments. Otherwise emit empty arrays
 *    (these rows get filtered out in the left pane, which only shows role strings with raw employees).
 * 3. For each raw bucket whose key has NO cert mapping: emit a RoleStringRow with isOrphan=true,
 *    jobs=[], position=null, role=null. The dropdown will start empty for those rows.
 */
export function useRoleStringRows(mappings: CertificationJobMapping[] | undefined, rawEmployees: HrisRawEmployeeRow[] | undefined): RoleStringRow[] {
	return useMemo(() => {
		const buckets = new Map<string, { employees: HrisRawEmployeeRow[]; internalDepartments: Set<string> }>();
		for (const e of rawEmployees ?? []) {
			const key = norm(e.role);
			if (!key) continue;
			let bucket = buckets.get(key);
			if (!bucket) {
				bucket = { employees: [], internalDepartments: new Set<string>() };
				buckets.set(key, bucket);
			}
			bucket.employees.push(e);
			bucket.internalDepartments.add(e.internalDepartment ?? "");
		}

		const seen = new Set<string>();
		const rows: RoleStringRow[] = [];
		for (const m of mappings ?? []) {
			const key = norm(m.roleString);
			seen.add(key);
			const bucket = buckets.get(key);
			rows.push({
				roleString: m.roleString,
				position: m.position ?? null,
				role: (m.role ?? null) as NurseRole | null,
				jobs: m.jobs,
				isOrphan: false,
				rawEmployees: bucket ? bucket.employees : [],
				internalDepartments: bucket ? Array.from(bucket.internalDepartments).sort() : [],
			});
		}

		for (const [key, bucket] of buckets.entries()) {
			if (seen.has(key)) continue;
			// Orphan: pick the original casing from the first raw employee for display.
			rows.push({
				roleString: bucket.employees[0]?.role ?? key,
				position: null,
				role: null,
				jobs: [],
				isOrphan: true,
				rawEmployees: bucket.employees,
				internalDepartments: Array.from(bucket.internalDepartments).sort(),
			});
		}

		return rows.sort((a, b) => a.roleString.localeCompare(b.roleString));
	}, [mappings, rawEmployees]);
}
